import { cn } from '@core/lib/cn';
import type { PriceBreakdownOut, PriceTotalsOut } from '@core/lib/api/types';
import { formatPaise, formatSheets, formatWeight } from '@core/lib/format';

export interface PriceBreakdownProps {
  breakdown: PriceBreakdownOut;
  totals: PriceTotalsOut;
  /** Open by default on the last step, where the customer is about to pay. */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * The itemised price, behind a disclosure.
 *
 * `<details>`/`<summary>` rather than a hand-rolled toggle: it is open/closed
 * without JavaScript, it is a real keyboard control with the platform's own
 * expanded state, and Ctrl+F on desktop opens it to reveal a match. A div with
 * an `onClick` and an `aria-expanded` is more code and less function.
 *
 * EVERY AMOUNT IS FORMATTED FROM `*_paise`. The API also sends pre-formatted
 * strings (`amount`, `total_display`) and we deliberately ignore them for
 * display: one formatter means the breakdown lines and the sticky total can
 * never disagree about grouping or the rupee symbol. The integers are the
 * contract; the strings are a convenience for logs.
 */
export function PriceBreakdown({
  breakdown,
  totals,
  defaultOpen = false,
  className,
}: PriceBreakdownProps) {
  return (
    <details
      className={cn('group rounded-card border border-line bg-surface', className)}
      open={defaultOpen}
    >
      <summary className="flex min-h-touch cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2">
        <span>Price breakdown</span>
        <span className="flex items-center gap-2 text-ink-muted">
          <span className="tabular-nums">{formatPaise(totals.total_paise)}</span>
          <span aria-hidden="true" className="transition-transform group-open:rotate-180">
            ⌄
          </span>
        </span>
      </summary>

      <div className="border-t border-line px-4 py-3">
        <dl>
          {breakdown.lines.map((line) => (
            <div key={line.code} className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="min-w-0 text-sm text-ink-muted">
                {line.label}
                {line.note ? (
                  <span className="block text-xs text-ink-subtle">{line.note}</span>
                ) : null}
              </dt>
              <dd
                className={cn(
                  'shrink-0 text-right text-sm font-medium tabular-nums',
                  line.kind === 'credit' ? 'text-success' : 'text-ink',
                )}
              >
                {/* A credit is sent as a positive amount with kind='credit'; the
                    sign is ours to render, and a discount that reads as a charge
                    is the kind of bug a customer notices before we do. */}
                {line.kind === 'credit' ? '−' : ''}
                {formatPaise(line.amount_paise)}
              </dd>
            </div>
          ))}

          <div className="mt-2 flex items-baseline justify-between gap-4 border-t border-line pt-3">
            <dt className="text-sm font-semibold text-ink">Total</dt>
            <dd className="text-right text-lg font-semibold tabular-nums text-ink">
              {formatPaise(totals.total_paise)}
            </dd>
          </div>
        </dl>

        <p className="mt-3 text-xs text-ink-subtle">
          {formatSheets(totals.sheets)} &middot; about {formatWeight(totals.weight_g)} to post
          {breakdown.pricing_version ? ` · price list ${breakdown.pricing_version}` : ''}
        </p>
      </div>
    </details>
  );
}
